import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic';

type LoadStatus = 'idle' | 'loading' | 'completed' | 'failed';

interface AlumnoCuentaState {
  resumen: any | null;
  resumenStatus: LoadStatus;
  resumenError: string | null;
  solicitudes: any[];
  solicitudesStatus: LoadStatus;
  solicitudesError: string | null;
  historico: any[];
  historicoStatus: LoadStatus;
  historicoError: string | null;
  activoDetalle: any | null;
  activoStatus: LoadStatus;
  activoError: string | null;
  finalizadoDetalle: any | null;
  finalizadoStatus: LoadStatus;
  finalizadoError: string | null;
}

const initialState: AlumnoCuentaState = {
  resumen: null,
  resumenStatus: 'idle',
  resumenError: null,
  solicitudes: [],
  solicitudesStatus: 'idle',
  solicitudesError: null,
  historico: [],
  historicoStatus: 'idle',
  historicoError: null,
  activoDetalle: null,
  activoStatus: 'idle',
  activoError: null,
  finalizadoDetalle: null,
  finalizadoStatus: 'idle',
  finalizadoError: null,
};

const parseData = (data: any) => {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (_) {
      return data;
    }
  }
  return data;
};

export const fetchAlumnoCuentaResumen = createAsyncThunk(
  'alumnoCuenta/fetchResumen',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 233 }, getState, dispatch);
    if (result) {
      return parseData(data);
    }
    return rejectWithValue(message || 'No fue posible obtener la información');
  }
);

export const fetchAlumnoSolicitudes = createAsyncThunk(
  'alumnoCuenta/fetchSolicitudes',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 232 }, getState, dispatch);
    if (result) {
      const parsed = parseData(data);
      return Array.isArray(parsed) ? parsed : [];
    }
    return rejectWithValue(message || 'No fue posible obtener las solicitudes');
  }
);

export const fetchAlumnoDiplomadosFinalizados = createAsyncThunk(
  'alumnoCuenta/fetchDiplomadosFinalizados',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 234 }, getState, dispatch);
    if (result) {
      const parsed = parseData(data);
      return Array.isArray(parsed) ? parsed : [];
    }
    return rejectWithValue(message || 'No fue posible obtener el historial');
  }
);

export const fetchAlumnoDiplomadoActivoDetalle = createAsyncThunk(
  'alumnoCuenta/fetchDiplomadoActivoDetalle',
  async ({ idCiclo }: { idCiclo: number }, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 236, idCiclo }, getState, dispatch);
    if (result) {
      return parseData(data);
    }
    return rejectWithValue(message || 'No fue posible obtener el detalle del diplomado');
  }
);

export const fetchAlumnoDiplomadoFinalizadoDetalle = createAsyncThunk(
  'alumnoCuenta/fetchDiplomadoFinalizadoDetalle',
  async ({ idCiclo }: { idCiclo: number }, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 235, idCiclo }, getState, dispatch);
    if (result) {
      return parseData(data);
    }
    return rejectWithValue(message || 'No fue posible obtener el detalle del diplomado');
  }
);

const alumnoCuentaSlice = createSlice({
  name: 'alumnoCuenta',
  initialState,
  reducers: {
    resetAlumnoCuenta: () => initialState,
    clearActivoDetalle: (state) => {
      state.activoDetalle = null;
      state.activoStatus = 'idle';
      state.activoError = null;
    },
    clearFinalizadoDetalle: (state) => {
      state.finalizadoDetalle = null;
      state.finalizadoStatus = 'idle';
      state.finalizadoError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlumnoCuentaResumen.pending, (state) => {
        state.resumenStatus = 'loading';
        state.resumenError = null;
      })
      .addCase(fetchAlumnoCuentaResumen.fulfilled, (state, action) => {
        state.resumenStatus = 'completed';
        state.resumen = action.payload || null;
      })
      .addCase(fetchAlumnoCuentaResumen.rejected, (state, action) => {
        state.resumenStatus = 'failed';
        state.resumenError = (action.payload as string) || 'Error al cargar la información';
      })
      .addCase(fetchAlumnoSolicitudes.pending, (state) => {
        state.solicitudesStatus = 'loading';
        state.solicitudesError = null;
      })
      .addCase(fetchAlumnoSolicitudes.fulfilled, (state, action) => {
        state.solicitudesStatus = 'completed';
        state.solicitudes = action.payload || [];
      })
      .addCase(fetchAlumnoSolicitudes.rejected, (state, action) => {
        state.solicitudesStatus = 'failed';
        state.solicitudesError = (action.payload as string) || 'Error al cargar solicitudes';
      })
      .addCase(fetchAlumnoDiplomadosFinalizados.pending, (state) => {
        state.historicoStatus = 'loading';
        state.historicoError = null;
      })
      .addCase(fetchAlumnoDiplomadosFinalizados.fulfilled, (state, action) => {
        state.historicoStatus = 'completed';
        state.historico = action.payload || [];
      })
      .addCase(fetchAlumnoDiplomadosFinalizados.rejected, (state, action) => {
        state.historicoStatus = 'failed';
        state.historicoError = (action.payload as string) || 'Error al cargar historial';
      })
      .addCase(fetchAlumnoDiplomadoActivoDetalle.pending, (state) => {
        state.activoStatus = 'loading';
        state.activoError = null;
      })
      .addCase(fetchAlumnoDiplomadoActivoDetalle.fulfilled, (state, action) => {
        state.activoStatus = 'completed';
        state.activoDetalle = action.payload || null;
      })
      .addCase(fetchAlumnoDiplomadoActivoDetalle.rejected, (state, action) => {
        state.activoStatus = 'failed';
        state.activoError = (action.payload as string) || 'Error al cargar detalle';
      })
      .addCase(fetchAlumnoDiplomadoFinalizadoDetalle.pending, (state) => {
        state.finalizadoStatus = 'loading';
        state.finalizadoError = null;
      })
      .addCase(fetchAlumnoDiplomadoFinalizadoDetalle.fulfilled, (state, action) => {
        state.finalizadoStatus = 'completed';
        state.finalizadoDetalle = action.payload || null;
      })
      .addCase(fetchAlumnoDiplomadoFinalizadoDetalle.rejected, (state, action) => {
        state.finalizadoStatus = 'failed';
        state.finalizadoError = (action.payload as string) || 'Error al cargar detalle';
      });
  },
});

export const {
  resetAlumnoCuenta,
  clearActivoDetalle,
  clearFinalizadoDetalle,
} = alumnoCuentaSlice.actions;

export const selectAlumnoCuentaResumen = (state: RootState) => state.alumnoCuenta.resumen;
export const selectAlumnoCuentaResumenStatus = (state: RootState) => state.alumnoCuenta.resumenStatus;
export const selectAlumnoCuentaResumenError = (state: RootState) => state.alumnoCuenta.resumenError;
export const selectAlumnoCuentaSolicitudes = (state: RootState) => state.alumnoCuenta.solicitudes;
export const selectAlumnoCuentaSolicitudesStatus = (state: RootState) => state.alumnoCuenta.solicitudesStatus;
export const selectAlumnoCuentaSolicitudesError = (state: RootState) => state.alumnoCuenta.solicitudesError;
export const selectAlumnoCuentaHistorico = (state: RootState) => state.alumnoCuenta.historico;
export const selectAlumnoCuentaHistoricoStatus = (state: RootState) => state.alumnoCuenta.historicoStatus;
export const selectAlumnoCuentaHistoricoError = (state: RootState) => state.alumnoCuenta.historicoError;
export const selectAlumnoCuentaActivoDetalle = (state: RootState) => state.alumnoCuenta.activoDetalle;
export const selectAlumnoCuentaActivoStatus = (state: RootState) => state.alumnoCuenta.activoStatus;
export const selectAlumnoCuentaActivoError = (state: RootState) => state.alumnoCuenta.activoError;
export const selectAlumnoCuentaFinalizadoDetalle = (state: RootState) => state.alumnoCuenta.finalizadoDetalle;
export const selectAlumnoCuentaFinalizadoStatus = (state: RootState) => state.alumnoCuenta.finalizadoStatus;
export const selectAlumnoCuentaFinalizadoError = (state: RootState) => state.alumnoCuenta.finalizadoError;

export default alumnoCuentaSlice.reducer;
